import {
  Box,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from 'new/components/AppBar/Sidebar';
import IconFontAwesome from 'new/components/IconFontAwesome';
import findRoute from 'new/routing/routes';
import React from 'react';
import { Link } from 'react-router';
import BillingIssueInfo from './components/BillingIssueInfo';
import StripeRestrictedInfo from './components/StripeRestrictedInfo';
import { useDisputesData } from './hooks';

const DisputesNotificationContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  width: '100%',
  paddingBottom: theme.spacing(2),

  '.notificationContent': {
    height: 100,
    width: '100%',
    backgroundColor: theme.palette.error.main,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: '#fff',

    '.notificationContentInner': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 100,

      a: {
        textDecoration: 'underline',
      },
    },
  },
}));

const TooltipContent = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '0.875rem',
  a: {
    color: theme.palette.grey[400],
  },
}));

const CustomTooltip = styled(
  ({ className, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}>
      {children}
    </Tooltip>
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.161)',
    padding: theme.spacing(1),

    '.MuiTooltip-arrow': {
      color: theme.palette.error.main,
    },
  },
}));

type Props = {
  isExpanded: boolean;
  isHovered: boolean;
};

const DisputesNotification: React.FC<Props> = ({ isExpanded, isHovered }) => {
  const {
    isVisibleDisputes,
    hasDisputes,
    stripeRestricted,
    billingIssue,
    remediationLink,
  } = useDisputesData();

  const hasWideLayout = isExpanded || isHovered;

  if (!isVisibleDisputes) return null;

  return (
    <DisputesNotificationContainer>
      <Box className="notificationContent">
        <Box
          className="notificationContentInner"
          width={
            hasWideLayout ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH
          }
        >
          {hasWideLayout ? (
            <>
              {hasDisputes && !stripeRestricted && (
                <Box textAlign="center">
                  You have a disputed
                  <br /> charge or charges
                  <br />
                  <Link to={findRoute('Disputes').path as string}>
                    Click to view.
                  </Link>
                </Box>
              )}
              {billingIssue && <BillingIssueInfo isBreakLine />}
              {stripeRestricted && (
                <CustomTooltip
                  placement="top-end"
                  title={
                    <TooltipContent>
                      <StripeRestrictedInfo remediationLink={remediationLink} />
                    </TooltipContent>
                  }
                >
                  <Box textAlign="center">
                    Your account has an issue with our payment processor,
                    Stripe.
                    <Typography fontWeight="bold">(view more)</Typography>
                  </Box>
                </CustomTooltip>
              )}
            </>
          ) : (
            <IconFontAwesome name="faExclamationCircle" />
          )}
        </Box>
      </Box>
    </DisputesNotificationContainer>
  );
};

export default DisputesNotification;
